import axios from "axios";

const PRIEMS_API = import.meta.env.VITE_PRIEMS_API_URL;

// ==================== Auth ====================

export const login = async (username, password) => {
  const { data } = await axios.post(`${PRIEMS_API}/v1/auth/login`, {
    username,
    password,
  });
  return data;
};

export const refreshToken = async (refreshTokenValue) => {
  const { data } = await axios.post(`${PRIEMS_API}/v1/auth/refresh`, {
    refreshToken: refreshTokenValue,
  });
  return data;
};

export const validateToken = async () => {
  const { data } = await axios.get(`${PRIEMS_API}/v1/auth/validate`);
  return data;
};

// ==================== Data Transform Helpers ====================

/**
 * 將 s3:// 開頭的 URL 轉換為 https://
 */
const fixS3Url = (url) => {
  if (typeof url === "string" && url.startsWith("s3://")) {
    return url.replace("s3://", "https://");
  }
  return url;
};

/**
 * 確保數值欄位為 number（API 可能回傳字串）
 */
const ensureNumber = (value) => {
  if (typeof value === "string") return parseFloat(value);
  return value;
};

/**
 * 轉換風機資料：確保 alertRange 為數值
 * 注意：API 已回傳正確 GeoJSON [lng, lat] 格式，不需翻轉座標
 */
const transformWindTurbine = (feature) => {
  if (feature.properties && feature.properties.alertRange) {
    feature.properties.alertRange = {
      inner: ensureNumber(feature.properties.alertRange.inner),
      outer: ensureNumber(feature.properties.alertRange.outer),
    };
  }
  return feature;
};

/**
 * 轉換雷達資料：確保 scanDistance 為數值 + 修正圖片 URL
 */
const transformRadar = (feature) => {
  if (feature.properties) {
    feature.properties.scanDistance = ensureNumber(
      feature.properties.scanDistance
    );
    feature.properties.image_url = fixS3Url(feature.properties.image_url);
  }
  return feature;
};

/**
 * 轉換攝影機資料：修正圖片 URL
 */
const transformCamera = (feature) => {
  if (feature.properties) {
    feature.properties.image_url = fixS3Url(feature.properties.image_url);
  }
  return feature;
};

// ==================== API Endpoints ====================

export const getWindTurbines = async () => {
  const { data } = await axios.get(`${PRIEMS_API}/v1/wind-turbines`);
  return {
    ...data,
    features: (data.features || []).map(transformWindTurbine),
  };
};

export const getHorizontalRadars = async () => {
  const { data } = await axios.get(`${PRIEMS_API}/v1/horizontal-radars`);
  return {
    ...data,
    features: (data.features || []).map(transformRadar),
  };
};

export const getCameras = async () => {
  const { data } = await axios.get(`${PRIEMS_API}/v1/cameras`);
  return {
    ...data,
    features: (data.features || []).map(transformCamera),
  };
};

export const getActiveAlertEvents = async () => {
  const { data } = await axios.get(`${PRIEMS_API}/v1/active-alert-events`);
  return data;
};

export const getActiveTracks = async (radarId) => {
  const { data } = await axios.get(
    `${PRIEMS_API}/v1/horizontal-radars/${radarId}/active-tracks`
  );
  return data;
};


// ==================== 歷史資料查詢（HR / HC） ====================
//
// 客戶 2026-08-20 交付之 Priems API v0.3.0 與當初 MISSING_API.md 的規格有落差，
// 本區負責「UI 篩選條件 → API query」與「API 回傳 → 前端表格欄位」的雙向轉接：
//
//   MISSING_API.md 原定            實際 API
//   /v1/history/radar          →  /v1/horizontal-radar-track-records
//   /v1/history/camera         →  /v1/camera-video-records
//   tempMin / tempMax          →  temperatureMin / temperatureMax
//   humidityMin / humidityMax  →  rhMin / rhMax
//   perPage                    →  pageSize
//   species                    →  birdSpecies
//   linkedCamera: 'yes'/'no'   →  hasCamera: true/false
//   alertRanges（風機名稱）      →  windTurbineIds（integer 陣列）
//   videoUrl（我方原推測）→ mediaUrl → media:{url,type}  ⚠️ 已被改兩次，見下方註記
//
// ⚠️ 媒體欄位已被客戶改過兩次，接的時候務必以實際 payload 為準：
//    v0.3.0 `videoUrl` → v0.4.0 `mediaUrl` → **v0.5.0 `media: { url, type }`**
//    `type` 為 enum `image` | `video`（渲染用的判別依據，不是 MIME type），
//    目前正式資料為 .gif（type=image），客戶表示未來可能改影片。
//
// ⚠️ track 座標自 v0.4.0 起為 RFC 7946 3D `[lng, lat, altitude]`，altitude 單位為公尺（海拔）。
//    客戶 2026-08-27 指定：**高度分布圖以「每條軌跡的高度平均值」統計**，
//    不是把軌跡上每個點分開計數（後者會讓點數多的長軌跡被過度加權）。
//
// ⚠️ 鳥種自 v0.5.0 起帶 `conservationLevel`（0=非保育類，1~3 為保育等級），
//    前端不再自行維護保育類名錄。
//
// 風向／飛行方向：API 與前端皆採 16 方位英文 enum（N/NNE/…/NNW），不需轉換。
// 鳥種：API /v1/bird-species 回傳 7 種，與前端 BIRD_SPECIES_OPTIONS 一致（前端多一項「未辨識」）。

export const COMPASS_16 = [
  "N", "NNE", "NE", "ENE",
  "E", "ESE", "SE", "SSE",
  "S", "SSW", "SW", "WSW",
  "W", "WNW", "NW", "NNW",
];

/**
 * 角度（0-360）轉 16 方位。
 * API 的 course 欄位回傳角度數值（如 245.8），但篩選參數與表格顯示皆用方位字串。
 */
export const degreesToCompass = (deg) => {
  if (deg === null || deg === undefined || Number.isNaN(Number(deg))) return null;
  const idx = Math.round((((Number(deg) % 360) + 360) % 360) / 22.5) % 16;
  return COMPASS_16[idx];
};

/** { startH, startM } → "HH:MM:00"（API 要求 time format） */
const toApiTime = (h, m) =>
  `${String(h ?? "00").padStart(2, "0")}:${String(m ?? "00").padStart(2, "0")}:00`;

/**
 * 陣列參數序列化：FastAPI 收的是 repeat 形式（windDirections=SW&windDirections=N），
 * 而 axios 預設會送成 windDirections[]=SW。
 *
 * ⚠️ 2026-08-21 實測：送 bracket 形式時 API 仍回 HTTP 200，但篩選條件被「靜默忽略」
 * （total 維持 60 = 未篩選），不會報錯。務必保留此 serializer。
 */
const repeatArrayParams = (params) => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => sp.append(key, item));
    } else if (value !== null && value !== undefined) {
      sp.append(key, value);
    }
  });
  return sp.toString();
};

/** 只在有值時才塞進 params，避免送出一堆 null 造成 422 */
const putIf = (params, key, value) => {
  if (value === null || value === undefined || value === "") return;
  if (Array.isArray(value) && value.length === 0) return;
  params[key] = value;
};

/**
 * 將 UI 篩選條件轉為 API query 參數（HR / HC 共用的部分）
 */
const buildCommonHistoryParams = (filters = {}) => {
  const p = {};
  if (!filters.dateDisabled && filters.dateRange) {
    putIf(p, "startDate", filters.dateRange.start);
    putIf(p, "endDate", filters.dateRange.end);
  }
  if (!filters.timeDisabled && filters.timeRange) {
    const { startH, startM, endH, endM } = filters.timeRange;
    putIf(p, "startTime", toApiTime(startH, startM));
    putIf(p, "endTime", toApiTime(endH, endM));
  }
  if (!filters.speedDisabled && filters.speed) {
    putIf(p, "speedMin", filters.speed.min);
    putIf(p, "speedMax", filters.speed.max);
  }
  if (!filters.tempDisabled && filters.temperature) {
    putIf(p, "temperatureMin", filters.temperature.min);
    putIf(p, "temperatureMax", filters.temperature.max);
  }
  if (!filters.humidityDisabled && filters.humidity) {
    // 前端叫 humidity，API 叫 rh
    putIf(p, "rhMin", filters.humidity.min);
    putIf(p, "rhMax", filters.humidity.max);
  }
  putIf(p, "windDirections", filters.windDirection);
  putIf(p, "birdSpecies", filters.birdSpecies);
  return p;
};

/** 'yes' / 'no' / '' → true / false / undefined */
const yesNoToBool = (v) => (v === "yes" ? true : v === "no" ? false : undefined);

/**
 * 查詢雷達歷史軌跡紀錄（HR）
 *
 * @param {object} filters   HistoryRadarFilter 的 filters 物件
 * @param {object} options   { page, pageSize, windTurbineIdMap }
 *   windTurbineIdMap: { 'WTG-01': 1, ... } —— UI 的風機標籤對應 API 的 integer id。
 *   API 的 windTurbineIds 只收 integer；未提供對照表時該條件會被略過（避免送出無效值）。
 */
export const searchRadarTrackRecords = async (filters = {}, options = {}) => {
  const {
    page = 1,
    pageSize = 20,
    windTurbineIdMap = null,
    windTurbineNameMap = null,
  } = options;
  const params = buildCommonHistoryParams(filters);

  putIf(params, "courses", filters.flightDirection);
  putIf(params, "hasCamera", yesNoToBool(filters.cameraLink));

  if (filters.alertZones && filters.alertZones.length > 0) {
    const ids = filters.alertZones
      .map((z) => (typeof z === "number" ? z : windTurbineIdMap?.[z]))
      .filter((id) => typeof id === "number");
    if (ids.length > 0) {
      params.windTurbineIds = ids;
      params.hasWindTurbine = true;
    } else if (import.meta.env.DEV) {
      console.warn(
        "[api] alertZones 無法對應到風機 id，該篩選條件已略過：",
        filters.alertZones
      );
    }
  }

  params.page = page;
  params.pageSize = pageSize;

  const { data } = await axios.get(
    `${PRIEMS_API}/v1/horizontal-radar-track-records`,
    { params, paramsSerializer: { serialize: repeatArrayParams } }
  );
  return {
    data: (data.data || []).map((r) =>
      transformRadarTrackRecord(r, windTurbineNameMap)
    ),
    pagination: data.pagination || { page, pageSize, total: 0, hasNext: false },
  };
};

/**
 * 取回符合條件的全部雷達軌跡紀錄（分頁自動翻完）
 *
 * 供圖表分析（CA）使用 —— 客戶不提供統計 API，五張圖皆由前端聚合，
 * 因此需要完整資料集而非單頁。設 maxRecords 上限避免資料量成長後拖垮瀏覽器。
 */
export const fetchAllRadarTrackRecords = async (filters = {}, options = {}) => {
  // ⚠️ API 限制 pageSize 上限 100，超過回 422（2026-08-26 實測）
  const { pageSize = 100, maxRecords = 5000, ...rest } = options;
  const all = [];
  let page = 1;
  let truncated = false;

  for (;;) {
    const res = await searchRadarTrackRecords(filters, {
      ...rest,
      page,
      pageSize,
    });
    all.push(...res.data);
    if (all.length >= maxRecords) {
      truncated = true;
      break;
    }
    if (!res.pagination?.hasNext) break;
    page += 1;
  }
  return { records: all.slice(0, maxRecords), truncated };
};

/**
 * 查詢攝影機影片紀錄（HC）
 */
export const searchCameraVideoRecords = async (filters = {}, options = {}) => {
  const { page = 1, pageSize = 20, windTurbineNameMap = null } = options;
  const params = buildCommonHistoryParams(filters);

  putIf(params, "hasRadar", yesNoToBool(filters.radarLink));
  if (!filters.birdCountDisabled && filters.birdCount) {
    putIf(params, "topBirdSpeciesCountMin", filters.birdCount.min);
    putIf(params, "topBirdSpeciesCountMax", filters.birdCount.max);
  }

  params.page = page;
  params.pageSize = pageSize;

  const { data } = await axios.get(`${PRIEMS_API}/v1/camera-video-records`, {
    params,
    paramsSerializer: { serialize: repeatArrayParams },
  });
  return {
    data: (data.data || []).map((r) =>
      transformCameraVideoRecord(r, windTurbineNameMap)
    ),
    pagination: data.pagination || { page, pageSize, total: 0, hasNext: false },
  };
};

/**
 * 取得風機選項與對照表（供 HR 篩選與顯示使用）
 *
 * API 的 windTurbineIds 只收 integer，而下拉選單需要 label；
 * 這裡一次備齊 options（給篩選元件）、idMap（label→id，送查詢用）、
 * nameMap（id→label，顯示警示範圍用）。
 */
export const getWindTurbineOptions = async () => {
  const geo = await getWindTurbines();
  const options = [];
  const idMap = {};
  const nameMap = {};
  (geo.features || []).forEach((f) => {
    const { id, name } = f.properties || {};
    if (id === undefined || id === null) return;
    const label = name || `風機${id}`;
    options.push({ label, value: label });
    idMap[label] = id;
    nameMap[id] = label;
  });
  return { options, idMap, nameMap };
};

/**
 * 取得鳥種清單（供篩選下拉選單使用）
 * v0.5.0 起帶 `conservationLevel`：0 = 非保育類，1~3 為保育等級。
 */
export const getBirdSpecies = async () => {
  const { data } = await axios.get(`${PRIEMS_API}/v1/bird-species`);
  return (data.data || []).map((b) => ({
    label: b.name,
    value: b.name,
    conservationLevel: b.conservationLevel ?? 0,
  }));
};

/**
 * API 雷達軌跡紀錄 → 前端表格欄位
 * 對齊 mock/historyRadarData.js 的欄位命名，讓 View 可直接替換資料來源。
 */
const ZONE_LABEL = { inner: "內圈", outer: "外圈" };

/** API 的 media 物件 → { url, type }；缺值時回 null，避免畫面出現空連結 */
const toMedia = (media) => {
  if (!media || !media.url) return null;
  return { url: fixS3Url(media.url), type: media.type ?? "image" };
};

/**
 * ISO 時間字串 → `YYYY-MM-DD HH:mm:ss`
 * API 回傳如 `2025-04-15T15:40:29+08:00`，表格直接內插會顯示原始 ISO 格式。
 */
const formatDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ` +
    `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
  );
};

/** 平均值，空陣列回 null */
const average = (nums) =>
  nums.length === 0 ? null : nums.reduce((a, b) => a + b, 0) / nums.length;

const transformRadarTrackRecord = (r, windTurbineNameMap = null) => ({
  id: r.id,
  radarId: r.radarId,
  recordTime: formatDateTime(r.timestamp),
  recordTimeRaw: r.timestamp,
  // API 的 track 是 RFC 7946 3D LineString：[lng, lat, altitude(公尺,海拔)]
  trackCoordinates: r.track?.coordinates ?? [],
  altitudes: (r.track?.coordinates ?? [])
    .map((c) => c[2])
    .filter((a) => typeof a === "number"),
  // 客戶指定：高度分布以「每條軌跡的平均高度」為單位統計
  avgAltitude: average(
    (r.track?.coordinates ?? []).map((c) => c[2]).filter((a) => typeof a === "number")
  ),
  speed: r.speed,
  bearing: r.course,
  flightDirection: degreesToCompass(r.course),
  // 表格以 join(', ') 顯示，故轉為字串；原始資料保留於 windTurbineCrossings
  alertZones: (r.windTurbineCrossings || []).map(
    (c) =>
      `${windTurbineNameMap?.[c.id] ?? `風機${c.id}`}-${ZONE_LABEL[c.zone] ?? c.zone}`
  ),
  windTurbineCrossings: r.windTurbineCrossings || [],
  temperature: r.temperature,
  humidity: r.rh, // API: rh
  windSpeed: r.windSpeed,
  windDirection: r.windDirection,
  trackImageUrl: fixS3Url(r.trackImageUrl),
  hasCameraLink: (r.cameraVideoRecords || []).length > 0,
  cameraVideoRecords: (r.cameraVideoRecords || []).map((c) => ({
    id: c.id,
    media: toMedia(c.media),
    birdSpecies: c.birdSpecies || [],
  })),
  // 表格直接內插顯示，故給字串；陣列版本另存 birdSpeciesList
  birdSpecies: [
    ...new Set(
      (r.cameraVideoRecords || [])
        .flatMap((c) => c.birdSpecies || [])
        .map((b) => b.name)
    ),
  ].join("、"),
  birdSpeciesList: [
    ...new Set(
      (r.cameraVideoRecords || [])
        .flatMap((c) => c.birdSpecies || [])
        .map((b) => b.name)
    ),
  ],
});

/**
 * API 攝影機影片紀錄 → 前端表格欄位
 */
const transformCameraVideoRecord = (r, windTurbineNameMap = null) => ({
  id: r.id,
  cameraId: r.cameraId,
  recordTime: formatDateTime(r.timestamp),
  recordTimeRaw: r.timestamp,
  maxBirdCount: r.topBirdSpeciesCount,
  temperature: r.temperature,
  humidity: r.rh,
  windSpeed: r.windSpeed,
  windDirection: r.windDirection,
  media: toMedia(r.media),
  hasRadarLink: !!r.horizontalRadarTrackRecord,
  radarLinkId: r.horizontalRadarTrackRecord?.id ?? null,
  radarLinkSpeed: r.horizontalRadarTrackRecord?.speed ?? null,
  // 「是否穿過警示範圍」（規格附件 2 末欄）
  // 客戶 2026-08-28 於 camera-video-records 的巢狀 horizontalRadarTrackRecord
  // 內補上 windTurbineCrossings；無連動雷達的紀錄該物件為 null。
  windTurbineCrossings:
    r.horizontalRadarTrackRecord?.windTurbineCrossings ?? [],
  alertZones: (r.horizontalRadarTrackRecord?.windTurbineCrossings ?? []).map(
    (c) =>
      `${windTurbineNameMap?.[c.id] ?? `風機${c.id}`}-${ZONE_LABEL[c.zone] ?? c.zone}`
  ),
  birdSpecies: (r.birdSpecies || []).map((b) => b.name).join("、"),
  birdSpeciesList: (r.birdSpecies || []).map((b) => b.name),
  birdSpeciesDetail: r.birdSpecies || [],
  // 保育類改由 API 的 conservationLevel 判定（0 = 非保育類）
  protectedSpecies: (r.birdSpecies || [])
    .filter((b) => (b.conservationLevel ?? 0) > 0)
    .map((b) => ({
      name: b.name,
      count: b.quantity,
      level: b.conservationLevel,
    })),
});

export default {
  login,
  refreshToken,
  validateToken,
  getWindTurbines,
  getHorizontalRadars,
  getCameras,
  getActiveAlertEvents,
  getActiveTracks,
  getBirdSpecies,
  getWindTurbineOptions,
  searchRadarTrackRecords,
  fetchAllRadarTrackRecords,
  searchCameraVideoRecords,
  degreesToCompass,
};
